import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useSelector } from "react-redux";
import {
  dateToString,
  orderPath,
  reportPath,
} from "../../../Utils";
import { Popup } from "../../../utils/Div";
import { serviceAgreementDaysUntilNextService } from "./utils";
import { Link, useNavigate } from "react-router-dom";
import SimplePrompt from "../../../utils/simplePrompt";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import {
  getServiceAgreementNextJobUi,
  nextJobDisabledReasonMessage,
  validateServiceAgreementCreateOrder,
  validateServiceAgreementCreateReport,
} from "./serviceAgreementNextJob";
import { FieldHint } from "./field-hint";
import { runCreateNextJobOperation } from "./runCreateNextJobOperation";

export const ServiceAgreementNewReportSection = (props) => {
  const { item, onRefresh } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentCompany = useSelector((state) => state.session.currentCompany);
  const [actionPending, setActionPending] = useState(false);

  const createReport = async () => {
    if (actionPending) return;
    setActionPending(true);
    setPromptOpen(false);
    try {
      await runCreateNextJobOperation({
      jobKind: "report",
      agreement: item,
      companyId: currentCompany?.id,
      t,
      onCreated: (data) => navigate(reportPath({ report: data })),
      onRefresh,
      });
    } finally {
      setActionPending(false);
    }
  };

  const createOrder = async () => {
    if (actionPending) return;
    setActionPending(true);
    setNewOrderPromptOpen(false);
    try {
      await runCreateNextJobOperation({
      jobKind: "order",
      agreement: item,
      companyId: currentCompany?.id,
      t,
      onCreated: (data) => navigate(orderPath({ order: data })),
      onRefresh,
      });
    } finally {
      setActionPending(false);
    }
  };

  const [promptOpen, setPromptOpen] = useState(false);
  const [newOrderPromptOpen, setNewOrderPromptOpen] = useState(false);

  const handleCreateReport = () => {
    if (actionPending) return;
    if (!validateServiceAgreementCreateReport(item)) return;
    setPromptOpen(true);
  };

  const handleCreateOrder = () => {
    if (actionPending) return;
    if (!validateServiceAgreementCreateOrder(item)) return;
    setNewOrderPromptOpen(true);
  };

  const ui = getServiceAgreementNextJobUi(item);
  const due = serviceAgreementDaysUntilNextService({ agreement: item });
  const days = item?.days_until_next_service;
  const urgency =
    days != null && days < 5
      ? "bg-rose-100 text-rose-800"
      : days != null && days < 15
        ? "bg-amber-100 text-amber-900"
        : "bg-emerald-100 text-emerald-800";

  const ctaClass =
    "inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold";

  const disabledCopy =
    ui.kind === "create_report"
      ? nextJobDisabledReasonMessage(ui.disabledReason, { forOrder: false })
      : ui.kind === "create_order"
        ? nextJobDisabledReasonMessage(ui.disabledReason, { forOrder: true })
        : null;

  return (
    <>
      <section className="overflow-hidden rounded-[18px] border border-slate-200 bg-white shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.06)]">
        <div className="border-b border-slate-100 bg-[#EAF3FD] px-5 py-4">
          <h3 className="flex items-center text-[15px] font-extrabold text-slate-900">
            {t("service.agreements.next_job.section_title")}
            <FieldHint content={t("service.agreements.next_job.section_hint")} />
          </h3>
        </div>
        <div className="space-y-4 px-5 py-5">
          {!item?.active_report?.id ? (
            <>
              <p className="text-sm text-slate-600">
                {t("service.agreements.next_job.next_on")}{" "}
                <span className="font-semibold text-slate-900">
                  {dateToString(item?.next_service_date)}
                </span>
              </p>
              <span
                className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${urgency}`}
              >
                {t("service.agreements.next_job.days_until", {
                  count: due.days,
                })}
              </span>
            </>
          ) : (
            <p className="text-sm font-semibold text-slate-800">
              {t("service.agreements.next_job.in_progress")}
            </p>
          )}

          {ui.kind === "missing_type" ? (
            <div
              className={`${ctaClass} cursor-default bg-slate-100 text-slate-500`}
            >
              {t("service.agreements.actions.missing_type")}
            </div>
          ) : null}

          {ui.kind === "cancelled" ? (
            <div
              className={`${ctaClass} cursor-default bg-slate-100 text-slate-500`}
              title={t("service.agreements.actions.err_cancelled")}
            >
              {t("service.agreements.actions.err_cancelled")}
            </div>
          ) : null}

          {ui.kind === "link_report" ? (
            <Link
              to={reportPath({ report: ui.report })}
              className={`${ctaClass} bg-[#1463C7] text-white hover:bg-[#0B3A78]`}
            >
              {t("service.agreements.actions.go_to_report")}
            </Link>
          ) : null}

          {ui.kind === "create_report" ? (
            <div className="space-y-2">
              <button
                type="button"
                disabled={ui.disabled || actionPending}
                onClick={() =>
                  !ui.disabled && !actionPending && handleCreateReport()
                }
                title={disabledCopy || undefined}
                aria-disabled={ui.disabled || actionPending}
                className={`${ctaClass} ${
                  ui.disabled || actionPending
                    ? "cursor-not-allowed bg-slate-200 text-slate-400"
                    : "bg-[#1463C7] text-white hover:bg-[#0B3A78]"
                }`}
              >
                <span>
                  {actionPending
                    ? t("service.agreements.actions.creating")
                    : t("service.agreements.actions.create_report")}
                </span>
                <Popup
                  content={t("service.agreements.actions.hint_create_report")}
                >
                  <InformationCircleIcon className="h-4 w-4" />
                </Popup>
              </button>
              {ui.disabled && disabledCopy ? (
                <p className="text-xs text-slate-600" role="status">
                  {disabledCopy}
                </p>
              ) : null}
            </div>
          ) : null}

          {ui.kind === "create_order" ? (
            <div className="space-y-2">
              <button
                type="button"
                disabled={ui.disabled || actionPending}
                onClick={() =>
                  !ui.disabled && !actionPending && handleCreateOrder()
                }
                title={disabledCopy || undefined}
                aria-disabled={ui.disabled || actionPending}
                className={`${ctaClass} ${
                  ui.disabled || actionPending
                    ? "cursor-not-allowed bg-slate-200 text-slate-400"
                    : "bg-[#1463C7] text-white hover:bg-[#0B3A78]"
                }`}
              >
                <span>
                  {actionPending
                    ? t("service.agreements.actions.creating")
                    : t("service.agreements.actions.create_order")}
                </span>
                <Popup
                  content={t("service.agreements.actions.hint_create_order")}
                >
                  <InformationCircleIcon className="h-4 w-4" />
                </Popup>
              </button>
              {ui.disabled && disabledCopy ? (
                <p className="text-xs text-slate-600" role="status">
                  {disabledCopy}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
      </section>

      <SimplePrompt
        open={promptOpen}
        onClose={() => !actionPending && setPromptOpen(false)}
        onAccept={() => createReport()}
        title={t("service.agreements.actions.prompt_report_title")}
        description={t("service.agreements.actions.prompt_report_body")}
      />

      <SimplePrompt
        open={newOrderPromptOpen}
        onClose={() => !actionPending && setNewOrderPromptOpen(false)}
        onAccept={() => createOrder()}
        title={t("service.agreements.actions.prompt_order_title")}
        description={t("service.agreements.actions.prompt_order_body")}
      />
    </>
  );
};
